import type { RealtimeChannel } from '@supabase/supabase-js';

import { entitlementService } from '../../features/monetization/services/entitlementService';
import { supabase } from '../../lib/supabase';
import { normalizeQuestionLocale } from '../utils/questionLocales';

// --- Supabase Client with AsyncStorage ---
// We're importing the supabase client from our main initialization
// to ensure we only have one instance of the client
import { sessionQuestionService } from './sessionQuestionService';
// Removed confusing imports - all question fetching logic now in this file

// Export the same supabase client for consistency
export { supabase };


// Question format interface
interface FormatTableQuestion {
  id: number;
  question: string;
  options: string[] | ChoiceOption[];
  answer: string;
  correct_answer: string;
  explanation: string;
  question_type: string;
  format_data: Record<string, unknown>;
  category_id: number;
  difficulty_id: number;
  min_selections?: number;
  max_selections?: number;
  partial_credit?: boolean;
}

interface ChoiceOption {
  id?: number;
  text?: string;
  content?: string;
  option?: string;
  position?: number;
  correct_position?: number;
  correct?: boolean;
  [key: string]: unknown;
}

interface OrderingItem {
  text?: string;
  [key: string]: unknown;
}

interface QuizRpcQuestionRecord {
  question_id: number;
  question_text: string | null;
  question_type: string;
  difficulty_id?: number | null;
  format_data?: Record<string, unknown> | null;
}

interface PresenceUserData {
  name: string;
  avatar_url?: string;
}

interface PresencePayload extends PresenceUserData {
  user_id: string;
  online_at: string;
}

type PresenceState = Record<string, PresencePayload[]>;
type BroadcastPayload = Record<string, unknown> | string | number | boolean | null;
type PlayerStatusPayload = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function isChoiceOption(value: unknown): value is ChoiceOption {
  return isRecord(value);
}

function toChoiceOptions(value: unknown): ChoiceOption[] {
  return Array.isArray(value) ? value.filter(isChoiceOption) : [];
}

function normalizeOrderingItems(value: unknown): ChoiceOption[] {
  const items = toChoiceOptions(value);
  if (items.length === 0) {
    return [];
  }

  const extractedPositions = items.map((item) => {
    const rawPosition =
      typeof item.position === 'number'
        ? item.position
        : typeof item.correct_position === 'number'
          ? item.correct_position
          : null;
    return rawPosition;
  });

  const hasCompleteNumericPositions = extractedPositions.every((position) => position !== null);
  const zeroBasedPositions =
    hasCompleteNumericPositions &&
    Math.min(...(extractedPositions as number[])) === 0 &&
    Math.max(...(extractedPositions as number[])) === items.length - 1;

  return items.map((item, index) => {
    const fallbackPosition = index + 1;
    const rawPosition =
      typeof item.position === 'number'
        ? item.position
        : typeof item.correct_position === 'number'
          ? item.correct_position
          : null;
    const normalizedPosition =
      rawPosition === null
        ? fallbackPosition
        : zeroBasedPositions
          ? rawPosition + 1
          : rawPosition;
    const resolvedText =
      typeof item.text === 'string'
        ? item.text
        : typeof item.content === 'string'
          ? item.content
          : typeof item.option === 'string'
            ? item.option
            : String(item.id ?? fallbackPosition);

    return {
      ...item,
      text: resolvedText,
      content: resolvedText,
      position: normalizedPosition,
      correct_position: normalizedPosition,
    };
  });
}

function toStringOptions(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function cleanMcqSingleOption(value: unknown): string {
  return getString(value).replace(/\?$/, '').trim();
}

function getLegacyMcqSingleOptions(formatData: Record<string, unknown>): string[] {
  return [
    formatData.option_a,
    formatData.option_b,
    formatData.option_c,
    formatData.option_d,
  ]
    .map(cleanMcqSingleOption)
    .filter((option): option is string => option.length > 0);
}

function getMcqSingleOptions(formatData: Record<string, unknown>): string[] {
  const explicitOptions = toStringOptions(formatData.options)
    .map(cleanMcqSingleOption)
    .filter((option): option is string => option.length > 0);

  if (explicitOptions.length > 0) {
    return explicitOptions;
  }

  return getLegacyMcqSingleOptions(formatData);
}

function getMcqSingleCorrectAnswer(
  formatData: Record<string, unknown>,
  options: string[]
): string {
  const explicitCorrectAnswer = cleanMcqSingleOption(formatData.correct_answer);
  if (explicitCorrectAnswer.length > 0) {
    return explicitCorrectAnswer;
  }

  const correctLetter = getString(formatData.correct_option).toLowerCase();
  const optionMap: Record<string, string> = {
    a: cleanMcqSingleOption(formatData.option_a),
    b: cleanMcqSingleOption(formatData.option_b),
    c: cleanMcqSingleOption(formatData.option_c),
    d: cleanMcqSingleOption(formatData.option_d),
  };

  if (correctLetter in optionMap && optionMap[correctLetter].length > 0) {
    return optionMap[correctLetter];
  }

  return options[0] || '';
}

function toOrderingItems(value: unknown): OrderingItem[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function getString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function hasUsableQuestionText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function filterQuestionsWithPrompt(
  questions: QuizRpcQuestionRecord[],
  source: 'smart' | 'mixed'
): QuizRpcQuestionRecord[] {
  const filtered = questions.filter((question) => hasUsableQuestionText(question.question_text));

  if (filtered.length !== questions.length) {
    const droppedIds = questions
      .filter((question) => !hasUsableQuestionText(question.question_text))
      .map((question) => question.question_id);

    console.warn(`[fetchQuestions] Dropped ${questions.length - filtered.length} ${source} questions with blank prompts`, {
      droppedIds,
    });
  }

  return filtered;
}

function getNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' ? value : fallback;
}

function getBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function getChoiceOptionText(option: ChoiceOption | string): string {
  if (typeof option === 'string') {
    return option;
  }

  if (typeof option.text === 'string') {
    return option.text;
  }

  if (typeof option.option === 'string') {
    return option.option;
  }

  return String(option);
}

// Helper functions for question transformation
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function generateMCQSingleExplanation(question: string, correctAnswer: string, allOptions: string[], categoryId: number): string {
  const categoryExplanations: { [key: number]: string } = {
    1: 'Old Testament',
    2: 'New Testament',  
    3: 'Biblical History',
    4: 'Biblical Characters',
    5: 'Biblical Teachings'
  };
  
  const categoryName = categoryExplanations[categoryId] || 'Biblical knowledge';
  const questionLower = question.toLowerCase();
  
  const isMiracleQuestion = questionLower.includes('miracle') || questionLower.includes('heal') || 
                           questionLower.includes('water') || questionLower.includes('wine') ||
                           questionLower.includes('bread') || questionLower.includes('fish');
  
  let baseExplanation = '';
  
  if (isMiracleQuestion) {
    baseExplanation = `The correct answer is "${correctAnswer}". This miracle showcases God's divine power and demonstrates His love and compassion for His people as recorded in Scripture.`;
  } else {
    baseExplanation = `The correct answer is "${correctAnswer}". This ${categoryName} knowledge deepens our understanding of God's character and His relationship with humanity throughout Scripture.`;
  }
  
  const selectionGuidance = ` Among the ${allOptions.length} options provided, this is the most biblically accurate answer.`;
  return baseExplanation + selectionGuidance;
}

function generateMCQMultipleExplanation(question: string, correctOptions: ChoiceOption[], allOptions: ChoiceOption[], categoryId: number): string {
  const categoryExplanations: { [key: number]: string } = {
    1: 'Old Testament',
    2: 'New Testament',  
    3: 'Biblical History',
    4: 'Biblical Characters',
    5: 'Biblical Teachings'
  };
  
  const categoryName = categoryExplanations[categoryId] || 'Biblical knowledge';
  const correctTexts = correctOptions.map(opt => getChoiceOptionText(opt));
  const correctCount = correctTexts.length;
  
  let baseExplanation = '';
  if (correctCount === 1) {
    baseExplanation = `The correct answer is "${correctTexts[0]}". This ${categoryName} concept is important for understanding God's character and His relationship with humanity.`;
  } else {
    baseExplanation = `The correct answers are: ${correctTexts.join(', ')}. These ${categoryName} concepts work together to provide a comprehensive understanding of biblical truth.`;
  }
  
  const selectionGuidance = correctCount > 1 
    ? ` Remember that this question requires selecting ${correctCount} correct answers out of ${allOptions.length} total options.`
    : ` This question has only one correct answer among the ${allOptions.length} options provided.`;
  
  return baseExplanation + selectionGuidance;
}

function generateTrueFalseExplanations(question: string, correctAnswer: boolean, categoryId: number): {
  explanation_true: string;
  explanation_false: string;
} {
  const categoryExplanations: { [key: number]: string } = {
    1: 'Old Testament',
    2: 'New Testament',  
    3: 'Biblical History',
    4: 'Biblical Characters',
    5: 'Biblical Teachings'
  };
  
  const categoryName = categoryExplanations[categoryId] || 'Biblical knowledge';
  
  return {
    explanation_true: `This statement is true according to ${categoryName} teachings and biblical records.`,
    explanation_false: `This statement is false based on ${categoryName} teachings and biblical evidence.`
  };
}

// Helper function to process fetched questions data
async function processFetchedQuestions(data: QuizRpcQuestionRecord[], categoryId: number): Promise<FormatTableQuestion[]> {
  if (!data || data.length === 0) {
    return [];
  }
  
  // Transform questions to expected format (extracted from original fetchQuestions)
  const transformedQuestions: Array<FormatTableQuestion | null> = data.map((record): FormatTableQuestion | null => {
    const formatData = toRecord(record.format_data);
    
    if (record.question_type === 'mcq_single') {
      // Handle MCQ Single format - convert option_a, option_b, etc. to options array
      const options = getMcqSingleOptions(formatData);
      const correctAnswer = getMcqSingleCorrectAnswer(formatData, options);
      const explanation = getString(formatData.explanation); // Now includes explanations from updated RPC
      
      return {
        id: record.question_id,
        question: record.question_text,
        options: options,
        answer: correctAnswer,
        correct_answer: correctAnswer,
        explanation: explanation,
        question_type: 'mcq_single',
        format_data: {
          options: options,
          correct_answer: correctAnswer,
          explanation: explanation
        },
        category_id: categoryId,
        difficulty_id: record.difficulty_id || 1
      };
    } else if (record.question_type === 'mcq_multiple') {
      // Handle MCQ Multiple format
      const options = toChoiceOptions(formatData.options);
      const correctOptions = options.filter((opt) => opt.correct);
      
      return {
        id: record.question_id,
        question: record.question_text,
        options: options,
        answer: correctOptions.map((opt) => getChoiceOptionText(opt)).join(', '),
        correct_answer: correctOptions.map((opt) => getChoiceOptionText(opt)).join(', '),
        explanation: getString(formatData.explanation),
        question_type: 'mcq_multiple',
        format_data: {
          options: options,
          min_selections: getNumber(formatData.min_selections),
          max_selections: getNumber(formatData.max_selections),
          partial_credit: getBoolean(formatData.partial_credit),
          explanation: getString(formatData.explanation)
        },
        category_id: categoryId,
        difficulty_id: record.difficulty_id || 1,
        min_selections: getNumber(formatData.min_selections),
        max_selections: getNumber(formatData.max_selections),
        partial_credit: getBoolean(formatData.partial_credit)
      };
    } else if (record.question_type === 'true_false') {
      // Handle True/False format
      const correctAnswer = getBoolean(formatData.correct_answer);
      const explanation = correctAnswer 
        ? getString(formatData.explanation_true)
        : getString(formatData.explanation_false);
        
      return {
        id: record.question_id,
        question: record.question_text,
        options: ['True', 'False'],
        answer: correctAnswer ? 'True' : 'False',
        correct_answer: correctAnswer ? 'True' : 'False',
        explanation: explanation || '',
        question_type: 'true_false',
        format_data: {
          correct_answer: correctAnswer,
          explanation_true: getString(formatData.explanation_true),
          explanation_false: getString(formatData.explanation_false),
          explanation: explanation
        },
        category_id: categoryId,
        difficulty_id: record.difficulty_id || 1
      };
    } else if (record.question_type === 'fill_blank') {
      // Handle Fill Blank format
      return {
        id: record.question_id,
        question: record.question_text,
        options: [], // Fill blank doesn't use options
        answer: '', // Will be filled by user
        correct_answer: '', // Processed from blanks
        explanation: getString(formatData.explanation),
        question_type: 'fill_blank',
        format_data: {
          template: getString(formatData.template),
          blanks: formatData.blanks,
          case_sensitive: getBoolean(formatData.case_sensitive),
          allow_partial_match: getBoolean(formatData.allow_partial_match),
          explanation: getString(formatData.explanation)
        },
        category_id: categoryId,
        difficulty_id: record.difficulty_id || 1
      };
    } else if (record.question_type === 'ordering') {
      // Handle Ordering format
      const normalizedItems = normalizeOrderingItems(formatData.items);
      return {
        id: record.question_id,
        question: record.question_text,
        options: normalizedItems, // Items to order
        answer: '', // Will be the correct order
        correct_answer: '', // Will be processed from items
        explanation: getString(formatData.explanation),
        question_type: 'ordering',
        format_data: {
          items: normalizedItems,
          instruction: getString(formatData.instruction),
          partial_credit: getBoolean(formatData.partial_credit),
          strict_ordering: getBoolean(formatData.strict_ordering),
          explanation: getString(formatData.explanation)
        },
        category_id: categoryId,
        difficulty_id: record.difficulty_id || 1
      };
    }
    
    return null;
  }).filter((question): question is FormatTableQuestion => question !== null);
  
  // Filter out placeholder/debug questions and deduplicate
  const validQuestions = transformedQuestions
    .filter((question): question is FormatTableQuestion => question !== null)
    .filter(q => !q.question.match(/^(Multiple choice|True\/false|Fill in|Ordering) question about .+ \(ID: \d+\)/i));

  // Deduplicate by question text to prevent same question appearing twice
  const seen = new Set<string>();
  const deduped = validQuestions.filter(q => {
    const key = q.question.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return deduped;
}

// Retry configuration for question fetching
const QUESTION_FETCH_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 5000,
  backoffMultiplier: 1.5
};

// Helper function to delay with exponential backoff
async function delayWithBackoff(attempt: number): Promise<void> {
  const delay = Math.min(
    QUESTION_FETCH_CONFIG.initialDelayMs * Math.pow(QUESTION_FETCH_CONFIG.backoffMultiplier, attempt),
    QUESTION_FETCH_CONFIG.maxDelayMs
  );
  console.log(`[fetchQuestions] Retry attempt ${attempt + 1}, waiting ${delay}ms...`);
  await new Promise(resolve => setTimeout(resolve, delay));
}

// Multi-language question fetching with backward compatibility and retry logic
export async function fetchQuestions({
  categoryId,
  difficultyId,
  limit = 10,
  languageCode,
  useSmartSelection = true,
  userId = null,
  retryCount = 0
}: {
  categoryId: number,
  difficultyId?: number|string,
  limit?: number,
  languageCode?: string,
  useSmartSelection?: boolean,
  userId?: string | null,
  retryCount?: number
}): Promise<FormatTableQuestion[]> {
  console.log('[fetchQuestions] Called with:', { categoryId, difficultyId, limit, languageCode, useSmartSelection, retryCount });

  try {
    // Get user's current language if not specified (backward compatible)
    const userLanguage = normalizeQuestionLocale(languageCode);
    console.log('[fetchQuestions] Using language:', userLanguage);
    
    // Try smart selection with session tracking if enabled and user is authenticated
    if (useSmartSelection && userId && sessionQuestionService.hasActiveSession()) {
      console.log('[fetchQuestions] Using smart selection with history tracking');
      
      const smartQuestions = await sessionQuestionService.getSmartQuestions(
        userId,
        Number(categoryId),
        Number(difficultyId) || 1,
        userLanguage,
        limit
      );
      
      if (smartQuestions && smartQuestions.length > 0) {
        const validSmartQuestions = filterQuestionsWithPrompt(
          smartQuestions as QuizRpcQuestionRecord[],
          'smart'
        );

        if (validSmartQuestions.length !== smartQuestions.length) {
          console.warn('[fetchQuestions] Smart selection returned blank prompts, falling back to standard RPC', {
            requestedLimit: limit,
            validCount: validSmartQuestions.length,
          });
        } else {
        // Smart questions are already in the correct format from get_questions_with_history RPC
        console.log('[fetchQuestions] Smart questions retrieved:', validSmartQuestions.length);
        
        // Transform to match expected interface format with proper options handling
        const processedQuestions = validSmartQuestions.map((q) => {
          const formatData = toRecord(q.format_data);
          
          // Handle different question format types
          let options: string[] = [];
          let correctAnswer: string = '';
          let explanation: string = '';
          
          if (q.question_type === 'mcq_single') {
            // MCQ Single: Convert option_a, option_b, etc. to options array
            options = getMcqSingleOptions(formatData);
            correctAnswer = getMcqSingleCorrectAnswer(formatData, options);
            explanation = getString(formatData.explanation); // Now includes explanations from RPC function
            
          } else if (q.question_type === 'mcq_multiple') {
            options = toChoiceOptions(formatData.options).map((option) => getChoiceOptionText(option));
            correctAnswer = getString(formatData.correct_answer);
            explanation = getString(formatData.explanation);
            
          } else if (q.question_type === 'true_false') {
            options = ['True', 'False'];
            correctAnswer = getBoolean(formatData.correct_answer) ? 'True' : 'False';
            explanation = getString(formatData.explanation_true) || getString(formatData.explanation_false);
            
          } else if (q.question_type === 'fill_blank') {
            options = [];
            const blanks = Array.isArray(formatData.blanks) ? formatData.blanks : [];
            const firstBlank = blanks[0];
            correctAnswer = isRecord(firstBlank) && typeof firstBlank.answer === 'string' ? firstBlank.answer : '';
            explanation = getString(formatData.explanation);
            
          } else if (q.question_type === 'ordering') {
            options = toOrderingItems(formatData.items).map((item) => (typeof item.text === 'string' ? item.text : String(item)));
            correctAnswer = '';
            explanation = getString(formatData.explanation);
          }
          
          return {
            id: q.question_id,
            question: q.question_text,
            question_type: q.question_type,
            format_data: formatData,
            options: options,
            answer: correctAnswer,
            correct_answer: correctAnswer,
            explanation: explanation,
            category_id: Number(categoryId),
            difficulty_id: Number(difficultyId) || 1
          };
        });
        
        return processedQuestions;
        }
      }
    }
    
    // Fallback to regular RPC call
    const rpcParams = {
      p_category_id: Number(categoryId),
      p_difficulty_id: Number(difficultyId) || 1,
      p_language_code: userLanguage,
      p_limit: limit
    };
    
    console.log('[fetchQuestions] Calling standard RPC with params:', rpcParams);
    
    // Test basic Supabase connection first
    console.log('[fetchQuestions] Testing Supabase connection...');
    try {
      const { data: testData, error: testError } = await supabase
        .from('format_questions_master')
        .select('id, question_type, category_id')
        .eq('category_id', Number(categoryId))
        .eq('difficulty_id', Number(difficultyId) || 1)
        .limit(1);
      
      console.log('[fetchQuestions] Connection test result:', { testData, testError });
    } catch (connError) {
      console.error('[fetchQuestions] Connection test failed:', connError);
    }
    
    // Clear any potential caching and call RPC function
    const { data, error } = await supabase
      .rpc('get_questions_for_quiz_mixed', rpcParams);

    console.log('[fetchQuestions] RPC response:', { 
      dataLength: data?.length || 0, 
      dataType: typeof data,
      dataIsArray: Array.isArray(data),
      rawData: data,
      error: error,
      errorDetails: error ? JSON.stringify(error, null, 2) : 'no error'
    });

    // Multi-language fallback logic
    if ((!data || data.length === 0) && userLanguage !== 'en' && !error) {
      console.log(`[fetchQuestions] No questions found for language '${userLanguage}', attempting English fallback...`);
      
      const fallbackParams = {
        ...rpcParams,
        p_language_code: 'en'
      };
      
      const { data: fallbackData, error: fallbackError } = await supabase
        .rpc('get_questions_for_quiz_mixed', fallbackParams);
        
      console.log('[fetchQuestions] English fallback response:', { 
        dataLength: fallbackData?.length || 0, 
        error: fallbackError 
      });
      
      if (fallbackData && fallbackData.length > 0) {
        console.log(`[fetchQuestions] ✅ Successfully fell back to English - found ${fallbackData.length} questions`);
        // Use fallback data but continue with original processing logic
        const processedData = fallbackData;
        
        // Process and return fallback questions
        return await processFetchedQuestions(processedData, Number(categoryId));
      } else {
        console.log('[fetchQuestions] ❌ English fallback also failed');
      }
    }
    
    // DEBUG: Log question fetch results
    if (data && Array.isArray(data) && data.length > 0) {
      const typeCounts: Record<string, number> = {};
      const langCodes = new Set<string>();
      (data as QuizRpcQuestionRecord[]).forEach((q) => {
        typeCounts[q.question_type] = (typeCounts[q.question_type] || 0) + 1;
        if (q.format_data?.language_code) langCodes.add(q.format_data.language_code);
      });
      console.log(`[fetchQuestions] Returned ${data.length} questions (locale=${userLanguage}): ${JSON.stringify(typeCounts)}, format_data locales: [${[...langCodes].join(', ')}]`);
    } else {
      console.log(`[fetchQuestions] No data returned for category=${categoryId}, difficulty=${difficultyId}, locale=${userLanguage}`);
    }
    
    if (error) {
      console.error('[fetchQuestions] RPC function error:', error);

      // Retry on error if we haven't exceeded max retries
      if (retryCount < QUESTION_FETCH_CONFIG.maxRetries) {
        console.log(`[fetchQuestions] RPC error, initiating retry ${retryCount + 1}/${QUESTION_FETCH_CONFIG.maxRetries}`);
        await delayWithBackoff(retryCount);
        return fetchQuestions({
          categoryId,
          difficultyId,
          limit,
          languageCode,
          useSmartSelection,
          userId,
          retryCount: retryCount + 1
        });
      }
      console.error('[fetchQuestions] Max retries exceeded, returning empty array');
      return [];
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log('[fetchQuestions] No data returned');

      // Retry if no data and we haven't exceeded max retries
      // This helps with transient network issues or database connection problems
      if (retryCount < QUESTION_FETCH_CONFIG.maxRetries) {
        console.log(`[fetchQuestions] No data returned, initiating retry ${retryCount + 1}/${QUESTION_FETCH_CONFIG.maxRetries}`);
        await delayWithBackoff(retryCount);
        return fetchQuestions({
          categoryId,
          difficultyId,
          limit,
          languageCode,
          useSmartSelection,
          userId,
          retryCount: retryCount + 1
        });
      }
      console.log('[fetchQuestions] Max retries exceeded with no data, returning empty array');
      return [];
    }

    const validRpcData = filterQuestionsWithPrompt(data as QuizRpcQuestionRecord[], 'mixed');

    if (validRpcData.length === 0) {
      if (retryCount < QUESTION_FETCH_CONFIG.maxRetries) {
        console.log(`[fetchQuestions] Mixed RPC returned only blank prompts, initiating retry ${retryCount + 1}/${QUESTION_FETCH_CONFIG.maxRetries}`);
        await delayWithBackoff(retryCount);
        return fetchQuestions({
          categoryId,
          difficultyId,
          limit,
          languageCode,
          useSmartSelection,
          userId,
          retryCount: retryCount + 1
        });
      }

      console.warn('[fetchQuestions] Mixed RPC returned only blank prompts after retries');
      return [];
    }

    if (validRpcData.length < data.length && retryCount < QUESTION_FETCH_CONFIG.maxRetries) {
      console.log(`[fetchQuestions] Mixed RPC dropped blank prompts, retrying for a full set ${retryCount + 1}/${QUESTION_FETCH_CONFIG.maxRetries}`);
      await delayWithBackoff(retryCount);
      return fetchQuestions({
        categoryId,
        difficultyId,
        limit,
        languageCode,
        useSmartSelection,
        userId,
        retryCount: retryCount + 1
      });
    }

    // Use helper function to process questions
    const transformedQuestions = await processComplexQuestions(validRpcData, Number(categoryId), Number(difficultyId));

    console.log(`[fetchQuestions] Successfully transformed ${transformedQuestions.length} questions`);
    console.log(`[fetchQuestions] Question types: ${transformedQuestions.map(q => q.question_type).join(', ')}`);
    
    return transformedQuestions;
    
  } catch (error) {
    console.error('[fetchQuestions] Error:', error);

    // Retry on exception if we haven't exceeded max retries
    if (retryCount < QUESTION_FETCH_CONFIG.maxRetries) {
      console.log(`[fetchQuestions] Exception caught, initiating retry ${retryCount + 1}/${QUESTION_FETCH_CONFIG.maxRetries}`);
      await delayWithBackoff(retryCount);
      return fetchQuestions({
        categoryId,
        difficultyId,
        limit,
        languageCode,
        useSmartSelection,
        userId,
        retryCount: retryCount + 1
      });
    }
    console.error('[fetchQuestions] Max retries exceeded after exception, returning empty array');
    return [];
  }
}

// Complete question processing function with all format support
async function processComplexQuestions(data: QuizRpcQuestionRecord[], categoryId: number, difficultyId: number): Promise<FormatTableQuestion[]> {
  const transformedQuestions: Array<FormatTableQuestion | null> = data.map((record): FormatTableQuestion | null => {
    const formatData = toRecord(record.format_data);
      
      if (record.question_type === 'mcq_single') {
        // Handle MCQ Single format
        const options = getMcqSingleOptions(formatData);
        const correctAnswer = getMcqSingleCorrectAnswer(formatData, options);
        
        const contextualExplanation = getString(formatData.explanation) || 
          generateMCQSingleExplanation(record.question_text, correctAnswer, options, Number(categoryId));
        
        return {
          id: record.question_id,
          question: record.question_text,
          options: getBoolean(formatData.shuffle_options) ? shuffleArray([...options]) : options,
          answer: correctAnswer,
          correct_answer: correctAnswer,
          explanation: contextualExplanation,
          question_type: 'mcq_single',
          format_data: {
            ...formatData,
            explanation: contextualExplanation
          },
          category_id: Number(categoryId),
          difficulty_id: Number(difficultyId) || 1
        };
      } else if (record.question_type === 'mcq_multiple') {
        // Handle MCQ Multiple format
        const rawOptions = toChoiceOptions(formatData.options);
        const options = rawOptions.map((opt, index: number) => ({
          ...opt,
          id: index + 1
        }));
        const correctOptions = options.filter((opt) => opt.correct);
        const correctAnswers = correctOptions.map((opt) => getChoiceOptionText(opt)).join(', ');
        
        const contextualExplanation = getString(formatData.explanation) || 
          generateMCQMultipleExplanation(record.question_text, correctOptions, options, Number(categoryId));
        
        return {
          id: record.question_id,
          question: record.question_text,
          options: options,
          answer: correctAnswers,
          correct_answer: correctAnswers,
          explanation: contextualExplanation,
          question_type: 'mcq_multiple',
          format_data: {
            ...formatData,
            options: options,
            explanation: contextualExplanation
          },
          category_id: Number(categoryId),
          difficulty_id: Number(difficultyId) || 1,
          min_selections: getNumber(formatData.min_selections),
          max_selections: getNumber(formatData.max_selections),
          partial_credit: getBoolean(formatData.partial_credit)
        };
      } else if (record.question_type === 'true_false') {
        // Handle True/False format
        const correctAnswer = getBoolean(formatData.correct_answer);
        const explanations = generateTrueFalseExplanations(
          record.question_text,
          correctAnswer,
          Number(categoryId)
        );
        
        return {
          id: record.question_id,
          question: record.question_text,
          options: ['True', 'False'],
          answer: correctAnswer ? 'True' : 'False',
          correct_answer: correctAnswer ? 'True' : 'False',
          explanation: getString(formatData.explanation) || explanations[correctAnswer ? 'explanation_true' : 'explanation_false'],
          question_type: 'true_false',
          format_data: {
            correct_answer: correctAnswer,
            explanation_true: getString(formatData.explanation_true) || explanations.explanation_true,
            explanation_false: getString(formatData.explanation_false) || explanations.explanation_false
          },
          category_id: Number(categoryId),
          difficulty_id: Number(difficultyId) || 1
        };
      } else if (record.question_type === 'fill_blank') {
        // Handle Fill Blank format
        return {
          id: record.question_id,
          question: record.question_text,
          options: [], // Fill blank doesn't use options
          answer: '', // Will be filled by user
          correct_answer: '', // Processed from blanks
          explanation: getString(formatData.explanation),
          question_type: 'fill_blank',
          format_data: {
            template: getString(formatData.template),
            blanks: formatData.blanks,
            case_sensitive: getBoolean(formatData.case_sensitive),
            allow_partial_match: getBoolean(formatData.allow_partial_match),
            explanation: getString(formatData.explanation)
          },
          category_id: Number(categoryId),
          difficulty_id: Number(difficultyId) || 1
        };
      } else if (record.question_type === 'ordering') {
        // Handle Ordering format
        const normalizedItems = normalizeOrderingItems(formatData.items);
        return {
          id: record.question_id,
          question: record.question_text,
          options: normalizedItems, // Items to order
          answer: '', // Will be the correct order
          correct_answer: '', // Will be processed from items
          explanation: getString(formatData.explanation),
          question_type: 'ordering',
          format_data: {
            items: normalizedItems,
            instruction: getString(formatData.instruction),
            partial_credit: getBoolean(formatData.partial_credit),
            strict_ordering: getBoolean(formatData.strict_ordering),
            explanation: getString(formatData.explanation)
          },
          category_id: Number(categoryId),
          difficulty_id: Number(difficultyId) || 1
        };
      }
      
      return null;
    });

  return transformedQuestions.filter((question): question is FormatTableQuestion => question !== null);
}

// === MULTIPLAYER FUNCTIONS ===

// Types for multiplayer functions
export interface CreateGameRoomParams {
  host_id: string;
  name: string;
  nickname?: string;
  difficulty_id: number;
  category_id: number;
  room_type?: string;
  avatar_url?: string;
  max_players?: number;
  public?: boolean;
  family_id?: string;
  friends_group_id?: string;
  church_id?: string;
  team_id?: string;
}

export interface JoinGameRoomParams {
  code: string;
  user_id: string;
  name: string;
  nickname?: string;
  avatar_url?: string;
}

export interface SetPlayerReadyParams {
  room_id: string;
  user_id: string;
  is_ready: boolean;
}

export interface StartGameSessionParams {
  room_id: string;
}

export interface SubmitGameAnswerParams {
  session_id: string;
  user_id: string;
  question_id: string;
  answer: string;
  is_correct: boolean;
}

export interface EndGameSessionParams {
  session_id: string;
}

// ChatMessage type
interface ChatMessage {
  id: string;
  user_id: string; 
  room_id: string;
  message: string;
  created_at: string;
}

// Create a new game room
export async function createGameRoom({
  host_id,
  name,
  nickname,
  difficulty_id,
  category_id,
  room_type = 'friends',
  family_id,
  friends_group_id,
  church_id,
  team_id,
  avatar_url,
  max_players = 4
}: CreateGameRoomParams) {
  try {
    const normalizedRoomType =
      family_id ? 'family' : friends_group_id ? 'friends' : room_type;

    const hostHasPremium = await entitlementService.hasPremiumHostAccess(host_id);
    if (!hostHasPremium) {
      max_players = 3; // Free tier: cap at 3 players (also enforced server-side via trigger)
    }

    console.log('[createGameRoom] Creating room with params:', {
      host_id, name, difficulty_id, category_id, room_type: normalizedRoomType
    });

    // Generate unique room code
    const roomCode = Math.random().toString(36).substring(2, 8).toUpperCase();

    // Map difficulty_id to difficulty string
    const difficultyMap: Record<number, string> = {
      1: 'easy',
      2: 'medium',
      3: 'hard'
    };
    const difficultyString = difficultyMap[difficulty_id] || 'medium';

    // Create the room (matching actual database schema)
    const { data: room, error: roomError } = await supabase
      .from('multiplayer_game_rooms')
      .insert({
        code: roomCode,
        host_id,
        status: 'waiting',
        category_id,
        difficulty_id,
        difficulty: difficultyString, // Add the difficulty string field
        public: false, // Using 'public' column name from schema
        max_players,
        room_type: normalizedRoomType,
        family_id,
        friends_group_id,
        church_id,
        team_id,
        language_code: 'en'
      })
      .select()
      .single();

    if (roomError) {
      console.error('[createGameRoom] Room creation error:', roomError);
      throw roomError;
    }

    if (!room) {
      throw new Error('No room data returned after creation');
    }

    console.log('[createGameRoom] Room created:', room.id, room.code);

    // Add host as the first player (using actual schema fields)
    const { error: playerError } = await supabase
      .from('multiplayer_game_room_players')
      .insert({
        room_id: room.id,
        user_id: host_id,
        name,
        nickname,
        avatar_url,
        is_ready: false
      });

    if (playerError) {
      console.error('[createGameRoom] Player addition error:', playerError);
      // Try to cleanup the room if player addition failed
      await supabase.from('multiplayer_game_rooms').delete().eq('id', room.id);
      throw playerError;
    }

    console.log('[createGameRoom] Host added as player successfully');
    return room;

  } catch (error) {
    console.error('[createGameRoom] Error:', error);
    throw error;
  }
}

// Join an existing game room
export async function joinGameRoom({ code, user_id, name, nickname, avatar_url }: JoinGameRoomParams) {
  try {
    // Find the room by code
    const { data: room, error: roomError } = await supabase
      .from('multiplayer_game_rooms')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (roomError || !room) {
      throw new Error('Room not found');
    }

    if (room.status !== 'waiting') {
      throw new Error('Room is no longer accepting players');
    }

    const { data: existingMembership, error: membershipError } = await supabase
      .from('multiplayer_game_room_players')
      .select('room_id')
      .eq('room_id', room.id)
      .eq('user_id', user_id)
      .maybeSingle();

    if (membershipError) {
      throw membershipError;
    }

    if (existingMembership?.room_id) {
      return room;
    }

    // Check if room is full by counting existing players
    const { count: playerCount } = await supabase
      .from('multiplayer_game_room_players')
      .select('*', { count: 'exact', head: true })
      .eq('room_id', room.id);

    if (playerCount && playerCount >= room.max_players) {
      throw new Error('Room is full');
    }

    // Add player to room (using actual schema fields)
    const { error: playerError } = await supabase
      .from('multiplayer_game_room_players')
      .insert({
        room_id: room.id,
        user_id,
        name,
        nickname,
        avatar_url,
        is_ready: false
        // Note: joined_at has default now(), is_host doesn't exist in schema
      });

    if (playerError) {
      if (playerError.code === '23505') {
        return room;
      }
      throw playerError;
    }

    return room;

  } catch (error) {
    console.error('[joinGameRoom] Error:', error);
    throw error;
  }
}

// Set player ready status
export async function setPlayerReady({ room_id, user_id, is_ready }: SetPlayerReadyParams) {
  try {
    const { error } = await supabase
      .from('multiplayer_game_room_players')
      .update({ is_ready })
      .eq('room_id', room_id)
      .eq('user_id', user_id);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('[setPlayerReady] Error:', error);
    throw error;
  }
}

export async function fetchSessionByRoomId(room_id: string) {
  const { data, error } = await supabase
    .from('multiplayer_game_sessions')
    .select('*')
    .eq('room_id', room_id)
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[fetchSessionByRoomId] Error:', error);
    throw error;
  }

  return data;
}

export async function startGameSession({ room_id }: StartGameSessionParams) {
  const { data, error } = await supabase
    .from('multiplayer_game_sessions')
    .insert({ room_id })
    .select('*')
    .single();

  if (error) {
    console.error('[startGameSession] Error:', error);
    throw error;
  }

  return data;
}

export async function submitGameAnswer({
  session_id,
  user_id,
  question_id,
  answer,
  is_correct,
}: SubmitGameAnswerParams) {
  const { error } = await supabase
    .from('multiplayer_game_answers')
    .insert({
      session_id,
      user_id,
      question_id,
      answer,
      is_correct,
    });

  if (error) {
    console.error('[submitGameAnswer] Error:', error);
    throw error;
  }
}

export async function endMultiplayerGameSession({ session_id }: EndGameSessionParams) {
  const { error } = await supabase
    .from('multiplayer_game_sessions')
    .update({ ended_at: new Date().toISOString() })
    .eq('id', session_id);

  if (error) {
    console.error('[endMultiplayerGameSession] Error:', error);
    throw error;
  }
}

export function listenToSession(session_id: string, onChange: () => void) {
  const channel = supabase
    .channel(`session-${session_id}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_sessions',
      filter: `id=eq.${session_id}`
    }, () => {
      onChange();
    })
    .subscribe();

  return channel;
}

export async function fetchAnswersBySessionId(session_id: string) {
  const { data, error } = await supabase
    .from('multiplayer_game_answers')
    .select('*')
    .eq('session_id', session_id)
    .order('answered_at', { ascending: true });

  if (error) {
    console.error('[fetchAnswersBySessionId] Error:', error);
    throw error;
  }

  return data || [];
}

export function listenToAnswers(session_id: string, onChange: () => void) {
  const channel = supabase
    .channel(`answers-${session_id}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_answers',
      filter: `session_id=eq.${session_id}`
    }, () => {
      onChange();
    })
    .subscribe();

  return channel;
}

// Fetch active rooms
export async function fetchActiveRooms({ publicOnly = false }: { publicOnly?: boolean } = {}) {
  try {
    let query = supabase
      .from('multiplayer_game_rooms')
      .select(`
        *,
        multiplayer_game_room_players!inner(count)
      `)
      .eq('status', 'waiting')
      .order('created_at', { ascending: false });

    if (publicOnly) {
      query = query.eq('is_public', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[fetchActiveRooms] Error:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[fetchActiveRooms] Error:', error);
    throw error;
  }
}

// Listen to active rooms changes
export function listenToActiveRooms(onChange: () => void) {
  console.log('[listenToActiveRooms] Setting up real-time listener');
  
  const channel = supabase
    .channel('active-rooms-changes')
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_rooms'
    }, (payload) => {
      console.log('[listenToActiveRooms] Room change detected:', payload);
      onChange();
    })
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_room_players'
    }, (payload) => {
      console.log('[listenToActiveRooms] Player change detected:', payload);
      onChange();
    })
    .subscribe();

  return channel;
}

// Listen to specific room changes
export function listenToRoom(room_id: string, onChange: () => void) {
  console.log('[listenToRoom] Setting up listener for room:', room_id);
  
  const channel = supabase
    .channel(`room-${room_id}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_rooms',
      filter: `id=eq.${room_id}`
    }, (payload) => {
      console.log('[listenToRoom] Room update:', payload);
      onChange();
    })
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_room_players',
      filter: `room_id=eq.${room_id}`
    }, (payload) => {
      console.log('[listenToRoom] Player update:', payload);
      onChange();
    })
    .subscribe();

  return channel;
}

// Listen to player status changes
export function listenToPlayerStatus(room_id: string, onPlayerChange: (payload: PlayerStatusPayload) => void) {
  console.log('[listenToPlayerStatus] Setting up listener for room:', room_id);
  
  const channel = supabase
    .channel(`players-${room_id}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'multiplayer_game_room_players',
      filter: `room_id=eq.${room_id}`
    }, (payload) => {
      console.log('[listenToPlayerStatus] Player status change:', payload);
      onPlayerChange(payload as PlayerStatusPayload);
    })
    .subscribe();

  return channel;
}

// Realtime presence functions
export function createMultiplayerRealtimeChannel(room_id: string) {
  return supabase.channel(`room_${room_id}`, {
    config: {
      presence: {
        key: room_id,
      },
    },
  });
}

export function joinRoomPresence(
  channel: RealtimeChannel,
  user_id: string,
  user_data: PresenceUserData
) {
  return channel.track({
    user_id,
    ...user_data,
    online_at: new Date().toISOString(),
  });
}

export function leaveRoomPresence(channel: RealtimeChannel) {
  return channel.untrack();
}

export function getPresenceState(channel: RealtimeChannel): PresenceState | undefined {
  return channel.presenceState() as PresenceState | undefined;
}

export function onPresenceSync(channel: RealtimeChannel, callback: (state: PresenceState) => void) {
  channel.on('presence', { event: 'sync' }, () => {
    callback(getPresenceState(channel) || {});
  });
}

export function sendBroadcast(channel: RealtimeChannel, event: string, payload: BroadcastPayload) {
  return channel.send({ type: 'broadcast', event, payload });
}

export function onBroadcast(channel: RealtimeChannel, event: string, callback: (payload: { payload: BroadcastPayload }) => void) {
  channel.on('broadcast', { event }, ({ payload }: { payload: BroadcastPayload }) => {
    callback({ payload });
  });
}

// Listen to chat messages
export function listenToChatMessages(roomId: string, onNewMessage: (message: ChatMessage) => void): (() => void) {
  const channel = supabase
    .channel(`room-chat-${roomId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public', 
      table: 'multiplayer_game_room_chat_messages',
      filter: `room_id=eq.${roomId}`
    }, (payload) => {
      onNewMessage(payload.new as ChatMessage);
    })
    .subscribe();
    
  // Return unsubscribe function
  return () => {
    channel.unsubscribe();
  };
}

// Fetch room players with their scores for leaderboard
export async function fetchRoomPlayersWithScores(roomId: string) {
  console.log('[fetchRoomPlayersWithScores] Fetching players for room:', roomId);
  
  try {
    const { data, error } = await supabase
      .from('multiplayer_game_room_players')
      .select(`
        id,
        user_id,
        name,
        nickname,
        avatar_url,
        score,
        time_taken_seconds,
        total_questions,
        finished_at,
        joined_at
      `)
      .eq('room_id', roomId);

    if (error) {
      console.error('[fetchRoomPlayersWithScores] Error:', error);
      throw error;
    }

    console.log(`[fetchRoomPlayersWithScores] Found ${data?.length || 0} players`);
    return data || [];

  } catch (error) {
    console.error('[fetchRoomPlayersWithScores] Failed:', error);
    throw error;
  }
}

// Subscribe to room players changes for real-time leaderboard updates
export function subscribeToRoomPlayersRealtime(roomId: string, onPlayersChange: () => void) {
  console.log('[subscribeToRoomPlayersRealtime] Setting up subscription for room:', roomId);
  
  const channel = supabase
    .channel(`room-players-${roomId}`)
    .on('postgres_changes', {
      event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
      schema: 'public',
      table: 'multiplayer_game_room_players',
      filter: `room_id=eq.${roomId}`
    }, (payload) => {
      console.log('[subscribeToRoomPlayersRealtime] Player change detected:', payload.eventType);
      onPlayersChange();
    })
    .subscribe();
    
  // Return unsubscribe function
  return () => {
    console.log('[subscribeToRoomPlayersRealtime] Unsubscribing from room players');
    channel.unsubscribe();
  };
}
